import type { ProjectAdd, ProjectUpdate } from "../models/project";
import { createCrudStoreBundle } from "./crud.store";
import { type ApiClient, type EntityWithId, runStoreAction } from "./shared";

/** Raw API project entity (round-trip via Project.raw in parsers). */
export interface ProjectEntity extends EntityWithId {
	readonly [key: string]: unknown;
}

export interface FindTaskByNameResult {
	readonly project: {
		readonly id: string;
		readonly name: string;
	};
	readonly task?: {
		readonly id: string;
		readonly name: string;
	} | null;
}

export const createProjectsStore = (apiClient: ApiClient) => {
	const bundle = createCrudStoreBundle<ProjectEntity, ProjectAdd, ProjectUpdate>({
		apiClient,
		entityPath: "projects",
	});

	const fetchAll = async (loadCompletedProjects = false, workspaceId?: string): Promise<readonly ProjectEntity[]> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const path = `/get/projects${loadCompletedProjects ? "" : "?$filter=completed eq false or completed eq null"}`;
			const headers = workspaceId ? { "workspace-id": workspaceId } : undefined;
			const projects = await apiClient.get<ProjectEntity[]>(path, undefined, headers);
			if (!workspaceId) {
				bundle.useStore.getState().setMany(projects);
			}
			return projects;
		});
	};

	const findByTaskName = async (name: string, userId?: string): Promise<readonly FindTaskByNameResult[]> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const filter = `name eq '${name.replace("'", "")}' and project__completed eq false and task__completed eq false${
				userId ? ` and user__id eq '${userId}'` : ""
			}`;
			const times = await apiClient.get<
				Array<{
					project: { id: string; name: string };
					task?: { id: string; name: string } | null;
				}>
			>("/get/times", {
				$filter: filter,
				$orderby: "start desc",
			});

			return times.map((time) => ({
				project: {
					id: time.project.id,
					name: time.project.name,
				},
				task: time.task ?? null,
			}));
		});
	};

	return {
		useStore: bundle.useStore,
		actions: {
			...bundle.actions,
			fetchAll,
			findByTaskName,
		},
	};
};
